import { memo } from 'react'
import { useTranslation } from 'react-i18next'
import profilePictureImage from '../../assets/images/setupProfileDefaultPicture.png'
import profilePictureImage2x from '../../assets/images/setupProfileDefaultPicture@2x.png'
import { ProfilePictureBackground } from './ProfilePictureBackground'
import './ProfilePictureIcon.scss'

interface ProfilePictureIconProps {
  size?: number
  className?: string
  selectedAvatarUrl?: string | null
  /**
   * When true (default), the icon grows to 2× on hover. Disable in
   * tight layouts (e.g. the Preferences profile row, where the 80px
   * slot sits against the left content edge and a 2× zoom would clip
   * into the SideNav border).
   */
  hoverZoom?: boolean
}

const ProfilePictureIconImpl = ({
  size = 80,
  className = '',
  selectedAvatarUrl = null,
  hoverZoom = true,
}: ProfilePictureIconProps) => {
  const { t } = useTranslation()
  const imageSize = size * 0.89 // 71.111 / 80 = 0.89

  return (
    <div
      className={`profile-picture-icon ${className}`.trim()}
      // Hover-zoom is gated on (a) having a real avatar — `data-has-avatar`
      // makes the default fallback PNG non-interactive — and (b) the
      // caller opting in via `hoverZoom`. Both must be true for the 2×
      // scale rule in SCSS to apply.
      data-has-avatar={selectedAvatarUrl ? 'true' : 'false'}
      data-hover-zoom={hoverZoom ? 'true' : 'false'}
      style={{ width: size, height: size }}
    >
      <ProfilePictureBackground className="profile-picture-icon__background" />
      <img
        src={selectedAvatarUrl || profilePictureImage}
        srcSet={selectedAvatarUrl ? undefined : `${profilePictureImage} 1x, ${profilePictureImage2x} 2x`}
        alt={t('avatarPicker.pictureAlt')}
        width={imageSize}
        height={imageSize}
        className="profile-picture-icon__image"
      />
    </div>
  )
}

// Pure leaf component — every prop is primitive or stable string, so
// shallow memo is sufficient to skip re-renders driven by ancestor
// state (e.g. the name-input keystrokes in AccountProfileSection).
export const ProfilePictureIcon = memo(ProfilePictureIconImpl)
